import { apiRequest } from '@/utils/api-request';
import {
  AccessRequestDecision,
  AccessRequestListResponse,
  AccessRequestStatus,
} from '@/types/share-types';
import * as Sentry from '@sentry/react';

/**
 * List pending access requests for a workflow.
 */
export async function listAccessRequests(
  orgId: string,
  workflowId: string,
  status: AccessRequestStatus = 'pending',
): Promise<AccessRequestListResponse> {
  try {
    const response = await apiRequest<{
      data?: AccessRequestListResponse;
      detail?: string;
    }>({
      url: `/api/v1/orgs/${orgId}/workflows/${workflowId}/access-requests?status=${status}`,
      target: 'workflow',
      method: 'GET',
    });

    if (response?.detail) {
      return { requests: [], count: 0 };
    }

    return (
      (response.data as unknown as AccessRequestListResponse) ?? {
        requests: [],
        count: 0,
      }
    );
  } catch (error) {
    Sentry.captureException(error, {
      extra: { context: 'list-access-requests', workflowId },
    });
    return { requests: [], count: 0 };
  }
}

/**
 * Approve or deny an access request.
 */
export async function reviewAccessRequest(
  orgId: string,
  workflowId: string,
  requestId: string,
  decision: AccessRequestDecision,
  role?: string,
): Promise<{ detail?: string }> {
  try {
    const response = await apiRequest<{ detail?: string }>({
      url: `/api/v1/orgs/${orgId}/workflows/${workflowId}/access-requests/${requestId}`,
      target: 'workflow',
      method: 'PUT',
      body: { resolution: decision, role },
    });

    return response;
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        context: 'review-access-request',
        workflowId,
        requestId,
        decision,
      },
    });
    throw error;
  }
}
